import { type Chamber } from 'components/us/common/chamber-select'
import { type State } from 'components/us/common/state-select'
import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import {
  type CommitteeAddress,
  type LeadershipAddress,
  type LegRole,
  type PersonBaseBasicInformation,
  type PersonUrl,
  type Role,
  type UsAddress
} from 'services'
import { type PersonAddress } from 'components'
import { getEmptyPersonAddress, getEmptyStafferEmail } from 'helpers'

export interface LegDistrict {
  id: number
  state: State
  legchamber: Chamber
  code: string
  desc: string
  type: string
  members: number
  counties: number
  mapLink: string
  updated_by?: string
  last_updated?: string
}

export interface LegSeatStatus {
  id: number
  statusCode: string
  statusDesc: string
  lutOrder: number
  updated_by?: string
  last_updated?: string
}

export interface LegSeat {
  id: number
  legdistrict: LegDistrict
  status: LegSeatStatus
  seatTrailer: string
  updated_by?: string
  last_updated?: string
}

export interface ChamberParty {
  id: number
  state?: {
    id: number
    state_code: string
    state_desc: string
  } | null
  masterCode?: string | null
  code?: string | null
  desc?: string | null
  lutOrder?: number | null
  updatedBy?: string | null
  lastUpdated?: string | null
}

export interface MemberInformation {
  id: number
  person: PersonBaseBasicInformation & { party?: ChamberParty | null }
  legrole: LegRole
  legseat: LegSeat
  majmin?: string
  chamber_majority_party?: ChamberParty | null
  chamber_minority_party?: ChamberParty | null
  chamberRank?: number
  percentVote?: number
  electDate?: string
  reelectYear?: string
  electCode?: string
  firstElectionDate?: string
  nextElectionDate?: string
  serviceStartDate?: string
  serviceEndDate?: string
  termNumber?: number
  fecLinkNum?: string | null
  prefAddress?: string
  pyracusc?: string
  cyracusc?: string
  pyradasc?: string
  cyradasc?: string
  pyraflsc?: string
  cyraflsc?: string
  pyruscosc?: string
  cyruscosc?: string
  updatedBy?: string
  lastUpdated?: string
}

export interface BaseStaffInformation {
  id: number
  person: PersonBaseBasicInformation
  role: Role
  title?: string
  address: PersonAddress
  statusId?: number | null
  email?: PersonUrl | null
  displayOrder: number
  updatedBy?: string
  lastUpdated?: string
  responsibilities: number[]
}

export interface LegStaffInformation extends BaseStaffInformation {
  legrole?: LegRole
  legrole2?: LegRole
  legrole3?: LegRole
}

export interface MemberStaffInformation extends LegStaffInformation {
  legmem: MemberInformation
  legmemOfficeLocationId: number
}

export interface MemberRankInformation {
  id: number
  person: PersonBaseBasicInformation
  legrole: LegRole
  legseat: LegSeat
  majmin?: string
  chamberRank?: number
}

export interface MemberRetireResponse {
  vacantPersonId: number
}

export interface MemberFillResponse {
  occupantPersonId: number
}

export interface TermLimitResponse {
  hasReachedLimit: boolean
  termLimit: number | null
}

export type MemberLevel = 'federal' | 'state'

export type MajorityMinorityType = 'MAJ' | 'MIN'

export const COMMITTEE_ADDRESS_DISPLAY_ORDER = {
  MAJORITY: 1,
  MINORITY: 2
} as const

export async function getMemberInfo (memberId: number): Promise<MemberInformation> {
  return await http
    .get(`/api/v1/us/member/${memberId}/`, {
    }).then(value => {
      return value.data
    })
}

const MEMBER_ADDRESS_TYPES = {
  FEDERAL_CAPITOL: 1,
  FEDERAL_DISTRICT: 2,
  STATE_CAPITOL: 13,
  STATE_DISTRICT: 14,
  TRANSITION: 67
} as const

export const OFFICE_LOCATIONS = {
  CAPITOL: 1,
  DISTRICT_1: 2,
  DISTRICT_2: 3,
  DISTRICT_3: 4,
  DISTRICT_4: 5,
  DISTRICT_5: 6,
  DISTRICT_6: 7,
  DISTRICT_7: 8,
  DISTRICT_8: 9,
  DISTRICT_9: 10,
  TRANSITION: 11,
  CAMPAIGN_1: 12,
  CAMPAIGN_2: 13,
  CAMPAIGN_3: 14
} as const

export const DISTRICT_OFFICE_LOCATIONS = [
  OFFICE_LOCATIONS.DISTRICT_1,
  OFFICE_LOCATIONS.DISTRICT_2,
  OFFICE_LOCATIONS.DISTRICT_3,
  OFFICE_LOCATIONS.DISTRICT_4,
  OFFICE_LOCATIONS.DISTRICT_5,
  OFFICE_LOCATIONS.DISTRICT_6,
  OFFICE_LOCATIONS.DISTRICT_7,
  OFFICE_LOCATIONS.DISTRICT_8,
  OFFICE_LOCATIONS.DISTRICT_9
] as const

function countFederalAddresses (memberAddresses: PersonAddress[]): { capitolCount: number, districtCount: number, transitionCount: number } {
  let capitolCount = 0
  let districtCount = 0
  let transitionCount = 0

  for (const address of memberAddresses) {
    if (address.typeId === MEMBER_ADDRESS_TYPES.FEDERAL_CAPITOL) {
      capitolCount++
    } else if (address.typeId === MEMBER_ADDRESS_TYPES.FEDERAL_DISTRICT) {
      districtCount++
    } else if (address.typeId === MEMBER_ADDRESS_TYPES.TRANSITION) {
      transitionCount++
    }
  }

  return { capitolCount, districtCount, transitionCount }
}

function buildOfficeLocationsList (capitolCount: number, districtCount: number, transitionCount: number): number[] {
  const availableLocations: number[] = []

  if (capitolCount > 0) {
    availableLocations.push(OFFICE_LOCATIONS.CAPITOL)
  }

  if (districtCount > 0) {
    for (let i = 0; i < Math.min(districtCount, DISTRICT_OFFICE_LOCATIONS.length); i++) {
      availableLocations.push(DISTRICT_OFFICE_LOCATIONS[i])
    }
  }

  if (transitionCount > 0) {
    availableLocations.push(OFFICE_LOCATIONS.TRANSITION)
  }

  return Array.from(new Set(availableLocations))
}

export function getAvailableFederalOfficeLocations (memberAddresses: PersonAddress[]): number[] {
  const { capitolCount, districtCount, transitionCount } = countFederalAddresses(memberAddresses)
  return buildOfficeLocationsList(capitolCount, districtCount, transitionCount)
}

function countStateAddresses (memberAddresses: PersonAddress[]): { capitolCount: number, districtCount: number, transitionCount: number } {
  let capitolCount = 0
  let districtCount = 0
  let transitionCount = 0

  for (const address of memberAddresses) {
    if (address.typeId === MEMBER_ADDRESS_TYPES.STATE_CAPITOL) {
      capitolCount++
    } else if (address.typeId === MEMBER_ADDRESS_TYPES.STATE_DISTRICT) {
      districtCount++
    } else if (address.typeId === MEMBER_ADDRESS_TYPES.TRANSITION) {
      transitionCount++
    }
  }

  return { capitolCount, districtCount, transitionCount }
}

export function getAvailableStateOfficeLocations (memberAddresses: PersonAddress[]): number[] {
  const { capitolCount, districtCount, transitionCount } = countStateAddresses(memberAddresses)
  return buildOfficeLocationsList(capitolCount, districtCount, transitionCount)
}

export function getAvailableOfficeLocations (memberAddresses: PersonAddress[], memberLevel: MemberLevel): number[] {
  return memberLevel === 'federal'
    ? getAvailableFederalOfficeLocations(memberAddresses)
    : getAvailableStateOfficeLocations(memberAddresses)
}

export function getFederalMemberAddressForOfficeLocation (
  memberAddresses: PersonAddress[],
  officeLocationId: number
): PersonAddress | null {
  if (officeLocationId === OFFICE_LOCATIONS.CAPITOL) {
    return memberAddresses.find(address => address.typeId === MEMBER_ADDRESS_TYPES.FEDERAL_CAPITOL) ?? null
  }

  if (officeLocationId === OFFICE_LOCATIONS.TRANSITION) {
    return memberAddresses.find(address => address.typeId === MEMBER_ADDRESS_TYPES.TRANSITION) ?? null
  }

  if (
    (officeLocationId >= OFFICE_LOCATIONS.DISTRICT_1 && officeLocationId <= OFFICE_LOCATIONS.DISTRICT_9) ||
    officeLocationId >= OFFICE_LOCATIONS.CAMPAIGN_1
  ) {
    const districtAddresses = memberAddresses.filter(address => address.typeId === MEMBER_ADDRESS_TYPES.FEDERAL_DISTRICT)

    let addressIndex = 0
    if (officeLocationId >= OFFICE_LOCATIONS.DISTRICT_1 && officeLocationId <= OFFICE_LOCATIONS.DISTRICT_9) {
      addressIndex = officeLocationId - OFFICE_LOCATIONS.DISTRICT_1
    } else if (officeLocationId >= OFFICE_LOCATIONS.CAMPAIGN_1) {
      addressIndex = officeLocationId - OFFICE_LOCATIONS.CAMPAIGN_1
    }

    return districtAddresses[addressIndex] ?? districtAddresses[0] ?? null
  }

  return null
}

export function getStateMemberAddressForOfficeLocation (
  memberAddresses: PersonAddress[],
  officeLocationId: number
): PersonAddress | null {
  if (officeLocationId === OFFICE_LOCATIONS.CAPITOL) {
    return memberAddresses.find(address => address.typeId === MEMBER_ADDRESS_TYPES.STATE_CAPITOL) ?? null
  }

  if (officeLocationId === OFFICE_LOCATIONS.TRANSITION) {
    return memberAddresses.find(address => address.typeId === MEMBER_ADDRESS_TYPES.TRANSITION) ?? null
  }

  if (
    (officeLocationId >= OFFICE_LOCATIONS.DISTRICT_1 && officeLocationId <= OFFICE_LOCATIONS.DISTRICT_9) ||
    officeLocationId >= OFFICE_LOCATIONS.CAMPAIGN_1
  ) {
    const districtAddresses = memberAddresses.filter(address => address.typeId === MEMBER_ADDRESS_TYPES.STATE_DISTRICT)

    let addressIndex = 0
    if (officeLocationId >= OFFICE_LOCATIONS.DISTRICT_1 && officeLocationId <= OFFICE_LOCATIONS.DISTRICT_9) {
      addressIndex = officeLocationId - OFFICE_LOCATIONS.DISTRICT_1
    } else if (officeLocationId >= OFFICE_LOCATIONS.CAMPAIGN_1) {
      addressIndex = officeLocationId - OFFICE_LOCATIONS.CAMPAIGN_1
    }

    return districtAddresses[addressIndex] ?? districtAddresses[0] ?? null
  }

  return null
}

export function getMemberAddressForOfficeLocation (
  memberAddresses: PersonAddress[],
  officeLocationId: number,
  memberLevel: MemberLevel
): PersonAddress | null {
  return memberLevel === 'federal'
    ? getFederalMemberAddressForOfficeLocation(memberAddresses, officeLocationId)
    : getStateMemberAddressForOfficeLocation(memberAddresses, officeLocationId)
}

export function getCommitteeAddressForMajorityMinority (
  committeeAddresses: CommitteeAddress[],
  majorityMinorityType: MajorityMinorityType
): CommitteeAddress | null {
  const targetDisplayOrder = majorityMinorityType === 'MAJ'
    ? COMMITTEE_ADDRESS_DISPLAY_ORDER.MAJORITY
    : COMMITTEE_ADDRESS_DISPLAY_ORDER.MINORITY
  return committeeAddresses.find(address => address.displayOrder === targetDisplayOrder) ?? null
}

export function getLeadershipAddress (
  leadershipAddress: any
): LeadershipAddress | null {
  return leadershipAddress ?? null
}

export const transformAddress = (data: any, prefix: string): UsAddress => ({
  building: data[`${prefix}Building`],
  room: data[`${prefix}Room`],
  addressOne: data[`${prefix}StreetAddress`],
  addressTwo: data[`${prefix}StreetAddressTwo`],
  city: data[`${prefix}City`],
  state: data[`${prefix}State`],
  country: data[`${prefix}Country`],
  zipCode: data[`${prefix}Zipcode`]
})

export const revertTransformAddress = (address: UsAddress, prefix: string): Record<string, any> => ({
  [`${prefix}Building`]: address.building,
  [`${prefix}Room`]: address.room,
  [`${prefix}StreetAddress`]: address.addressOne,
  [`${prefix}StreetAddressTwo`]: address.addressTwo,
  [`${prefix}City`]: address.city,
  [`${prefix}State`]: address.state,
  [`${prefix}Country`]: address.country,
  [`${prefix}Zipcode`]: address.zipCode
})

export async function getStaffByMember (memberId: number): Promise<MemberStaffInformation[]> {
  return await http.get(`/api/v1/us/member/${memberId}/staff`).then(response => {
    return response.data.map((data: any) => {
      return {
        ...data,
        email: data.email != null
          ? {
              id: data.email.id,
              value: data.email.value,
              personId: data.person.id,
              lastUpdated: data.email.lastUpdated,
              typeId: 1,
              descId: data.legmemOfficeLocationId === 1 ? 8 : 13,
              displayOrder: data.email.displayOrder
            }
          : getEmptyStafferEmail(data.person.id),
        address: data.address != null
          ? {
              id: data.address.id,
              typeId: data.address.typeId,
              primary: transformAddress(data.address, 'primary'),
              secondary: transformAddress(data.address, 'secondary'),
              mailingAddress: data.address.mailingAddress,
              phones: data.address.phones,
              displayOrder: data.address.displayOrder,
              updatedBy: data.address.updatedBy,
              lastUpdated: data.address.lastUpdated
            }
          : getEmptyPersonAddress(data.person.id)
      }
    })
  })
}

export async function updateStafferDisplayOrder (memberId: number, IdsAnddisplayOrders: Record<number, number>, selectedId: number, roleType: number): Promise<HttpResponse<[]>> {
  const payload = {
    idAndDisplayOrderMap: IdsAnddisplayOrders,
    selectedId,
    roleType
  }
  return await handleHttpRequest(http.patch(`/api/v1/us/member/${memberId}/staff/`, payload))
}

export async function retireStafferFromMember (memberId: number, stafferId: number): Promise<HttpResponse<void>> {
  return await handleHttpRequest(http.delete(`/api/v1/us/member/${memberId}/staff/${stafferId}/`))
}

export async function updateMemberInfo (memberId: number, payload: MemberInformation): Promise<HttpResponse<MemberInformation>> {
  return await http.put(`/api/v1/us/member/${memberId}/`, payload)
    .then(response => response.data)
}

export async function checkTermLimit (
  stateId: number,
  chamberId: number,
  termNumber: number
): Promise<HttpResponse<TermLimitResponse>> {
  return await http.get('/api/v1/us/term-limit/', {
    params: { state: stateId, chamber: chamberId, termNumber }
  })
}

export function transformResponseAddress (address: any): PersonAddress {
  return {
    id: address.id,
    typeId: address.typeId,
    personId: address.personId,
    primary: transformAddress(address, 'primary'),
    secondary: transformAddress(address, 'secondary'),
    mailingAddress: address.mailingAddress,
    phones: address.phones,
    displayOrder: address.displayOrder,
    updatedBy: address.updatedBy,
    lastUpdated: address.lastUpdated
  }
}

export function transformStafferRequestCommon (staffer: BaseStaffInformation): Record<string, any> {
  const result: Record<string, any> = {
    ...staffer,
    person: {
      ...staffer.person,
      prefixId: staffer.person.prefixId ?? null,
      firstName: staffer.person.firstName ?? null,
      middleName: staffer.person.middleName ?? null,
      lastName: staffer.person.lastName ?? null,
      suffixId: staffer.person.suffixId ?? null,
      profSuffix: staffer.person.profSuffix ?? null,
      legalName: staffer.person.legalName ?? null,
      nickname: staffer.person.nickname ?? null,
      pronunciation: staffer.person.pronunciation ?? null,
      ethnicId: staffer.person.ethnicId ?? null,
      genderId: staffer.person.genderId ?? null,
      maritalStatusId: staffer.person.maritalStatusId ?? null,
      partyId: staffer.person.partyId ?? null,
      religionId: staffer.person.religionId ?? null,
      birthYear: staffer.person.birthYear ?? null,
      birthMonth: staffer.person.birthMonth ?? null,
      birthplace: staffer.person.birthplace ?? null,
      deathYear: staffer.person.deathYear ?? null,
      spouseName: staffer.person.spouseName ?? null,
      residence: staffer.person.residence ?? null,
      family: staffer.person.family ?? null,
      lastUpdated: staffer.person.lastUpdated ?? null
    },
    address: {
      id: staffer.address.id,
      typeId: 0,
      personId: staffer.person.id,
      ...revertTransformAddress(staffer.address.primary, 'primary'),
      ...revertTransformAddress(staffer.address.secondary, 'secondary'),
      mailingAddress: staffer.address.mailingAddress,
      phones: staffer.address.phones.map(phone => ({
        ...phone,
        id: phone.id != null && phone.id < 0 ? 0 : phone.id
      })),
      displayOrder: staffer.address.displayOrder,
      updatedBy: staffer.address.updatedBy,
      lastUpdated: staffer.address.lastUpdated
    }
  }

  if (staffer.email != null) {
    result.email = {
      id: (staffer.email.id === 0 || staffer.email.id === undefined) ? null : staffer.email.id,
      value: staffer.email.value ?? '',
      personId: staffer.person.id ?? 0,
      lastUpdated: staffer.email.lastUpdated ?? null,
      typeId: 1,
      descId: staffer.email.descId ?? 0,
      displayOrder: staffer.email.displayOrder ?? null
    }
  }
  return result
}

export function transformRequestStaffer (staffer: LegStaffInformation): Record<string, any> {
  return {
    ...transformStafferRequestCommon(staffer),
    legrole: staffer.legrole ?? null,
    legrole2: staffer.legrole2 ?? null,
    legrole3: staffer.legrole3 ?? null
  }
}

export async function updateLegStaffer (staffer: MemberStaffInformation): Promise<HttpResponse<MemberStaffInformation>> {
  const response = await http.put(
    `/api/v1/us/member/${staffer.legmem.id}/staff/${staffer.id}/`,
    transformRequestStaffer(staffer)
  )

  return {
    ...response.data,
    result: {
      ...response.data.result,
      address: transformResponseAddress(response.data.result.address)
    }
  }
}

export async function createLegStaffer (staffer: MemberStaffInformation): Promise<HttpResponse<MemberStaffInformation>> {
  const response = await http.post(
    `/api/v1/us/member/${staffer.legmem.id}/staff/`,
    transformRequestStaffer(staffer)
  )

  return {
    ...response.data,
    result: {
      ...response.data.result,
      address: transformResponseAddress(response.data.result.address)
    }
  }
}

export async function retireMember (memberId: number): Promise<HttpResponse<MemberRetireResponse>> {
  return await http.delete(`/api/v1/us/member/${memberId}/`, {}).then(response => response.data)
}

export async function fillMember (memberId: number, staffToKeep: number[], person: PersonBaseBasicInformation): Promise<HttpResponse<MemberFillResponse>> {
  const payload = {
    staffToKeep,
    person
  }
  return await handleHttpRequest(http.post(`/api/v1/us/member/${memberId}/`, payload))
}
